import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  ExportConfig,
  ExportFormat,
  ExportProgress,
  ExportRequest,
  ExportResult,
} from "@/types/export";

class ExportServiceClass {
  private defaultConfig: ExportConfig = {
    format: "csv",
    filename: "export",
    includeHeaders: true,
    maxRecords: 10000,
    dateFormat: "YYYY-MM-DD",
    delimiter: ",",
  };

  /**
   * Export data to CSV format
   */
  private async exportToCSV(
    data: any[],
    config: ExportConfig,
    onProgress?: (progress: ExportProgress) => void,
  ): Promise<ExportResult> {
    try {
      const totalRecords = Math.min(data.length, config.maxRecords || 10000);

      onProgress?.({
        status: "preparing",
        totalRecords,
        processedRecords: 0,
        percentage: 0,
        message: "Preparing CSV export...",
      });

      const dataToExport = data.slice(0, totalRecords);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
        header: config.fields,
        skipHeader: !config.includeHeaders,
      });

      onProgress?.({
        status: "processing",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 50,
        message: "Processing data...",
      });

      const csv = XLSX.utils.sheet_to_csv(worksheet, {
        FS: config.delimiter || ",",
      });

      onProgress?.({
        status: "downloading",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 75,
        message: "Generating file...",
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const filename = `${config.filename}.csv`;
      saveAs(blob, filename);

      onProgress?.({
        status: "completed",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 100,
        message: "Export completed successfully",
      });

      return {
        success: true,
        filename,
        recordsExported: totalRecords,
        fileSize: blob.size,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "CSV export failed";

      onProgress?.({
        status: "error",
        totalRecords: 0,
        processedRecords: 0,
        percentage: 0,
        error: errorMessage,
      });

      return {
        success: false,
        filename: config.filename,
        recordsExported: 0,
        error: errorMessage,
      };
    }
  }

  /**
   * Export data to Excel format
   */
  private async exportToExcel(
    data: any[],
    config: ExportConfig,
    onProgress?: (progress: ExportProgress) => void,
  ): Promise<ExportResult> {
    try {
      const totalRecords = Math.min(data.length, config.maxRecords || 10000);

      onProgress?.({
        status: "preparing",
        totalRecords,
        processedRecords: 0,
        percentage: 0,
        message: "Preparing Excel export...",
      });

      const dataToExport = data.slice(0, totalRecords);
      const worksheet = XLSX.utils.json_to_sheet(dataToExport, {
        header: config.fields,
        skipHeader: !config.includeHeaders,
      });

      onProgress?.({
        status: "processing",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 50,
        message: "Processing data...",
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

      onProgress?.({
        status: "downloading",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 75,
        message: "Generating file...",
      });

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const filename = `${config.filename}.xlsx`;
      saveAs(blob, filename);

      onProgress?.({
        status: "completed",
        totalRecords,
        processedRecords: totalRecords,
        percentage: 100,
        message: "Export completed successfully",
      });

      return {
        success: true,
        filename,
        recordsExported: totalRecords,
        fileSize: blob.size,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Excel export failed";

      onProgress?.({
        status: "error",
        totalRecords: 0,
        processedRecords: 0,
        percentage: 0,
        error: errorMessage,
      });

      return {
        success: false,
        filename: config.filename,
        recordsExported: 0,
        error: errorMessage,
      };
    }
  }

  /**
   * Export data with specified format and configuration
   */
  async exportData(
    request: ExportRequest,
    onProgress?: (progress: ExportProgress) => void,
  ): Promise<ExportResult> {
    const config: ExportConfig = {
      ...this.defaultConfig,
      ...request.config,
      format: request.format,
      filename: request.filename,
    };

    if (!request.data || request.data.length === 0) {
      const error = "No data to export";
      onProgress?.({
        status: "error",
        totalRecords: 0,
        processedRecords: 0,
        percentage: 0,
        error,
      });
      return {
        success: false,
        filename: config.filename,
        recordsExported: 0,
        error,
      };
    }

    if (request.format === "csv") {
      return this.exportToCSV(request.data, config, onProgress);
    } else if (request.format === "excel") {
      return this.exportToExcel(request.data, config, onProgress);
    }

    const error = `Unsupported export format: ${request.format}`;
    onProgress?.({
      status: "error",
      totalRecords: 0,
      processedRecords: 0,
      percentage: 0,
      error,
    });
    return {
      success: false,
      filename: config.filename,
      recordsExported: 0,
      error,
    };
  }

  /**
   * Validate export request
   */
  validateExportRequest(request: ExportRequest): {
    valid: boolean;
    error?: string;
  } {
    if (!request.format) {
      return { valid: false, error: "Export format is required" };
    }

    if (!["csv", "excel"].includes(request.format)) {
      return {
        valid: false,
        error: "Invalid export format. Must be csv or excel",
      };
    }

    if (!request.data || !Array.isArray(request.data)) {
      return { valid: false, error: "Data must be a valid array" };
    }

    if (request.data.length === 0) {
      return { valid: false, error: "No data to export" };
    }

    if (!request.filename || request.filename.trim() === "") {
      return { valid: false, error: "Filename is required" };
    }

    const maxRecords =
      request.config?.maxRecords || this.defaultConfig.maxRecords || 10000;
    if (request.data.length > maxRecords) {
      return {
        valid: true,
        error: `Data will be limited to ${maxRecords} records`,
      };
    }

    return { valid: true };
  }

  /**
   * Get export progress status
   */
  getInitialProgress(): ExportProgress {
    return {
      status: "idle",
      totalRecords: 0,
      processedRecords: 0,
      percentage: 0,
    };
  }
}

const ExportService = new ExportServiceClass();
export default ExportService;
